/**
 * DataTable Component
 * Displays query results in a collapsible, scrollable table
 */

import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';

const DataTable = ({ data, title = "Query Results" }) => {
  const [expanded, setExpanded] = useState(true);
  const [copiedTooltip, setCopiedTooltip] = useState(false);

  // Parse the data - handle the new JSON structure from backend
  const { rows, columns } = useMemo(() => {
    if (!data) return { rows: [], columns: [] };
    
    // Handle the new structure from agent_service conversion
    // Format: { data: [...], columns: [...], row_count: n, column_count: m }
    if (data.data && data.columns) {
      return { 
        rows: data.data, 
        columns: data.columns 
      };
    }
    
    // Fallback: If data is already an array of objects (legacy format)
    if (Array.isArray(data)) {
      const cols = data.length > 0 ? Object.keys(data[0]) : [];
      return { rows: data, columns: cols };
    }
    
    // Fallback: If data has rows/columns structure
    if (data.rows && data.columns) {
      return { rows: data.rows, columns: data.columns };
    }
    
    // Fallback: If data is a single object with column arrays
    // Convert from columnar format to row format
    const cols = Object.keys(data);
    const rowCount = cols.length > 0 ? data[cols[0]].length : 0;
    const rowData = [];
    
    for (let i = 0; i < rowCount; i++) {
      const row = {};
      cols.forEach(col => {
        row[col] = data[col][i];
      });
      rowData.push(row);
    }
    
    return { rows: rowData, columns: cols };
  }, [data]);

  // Copy table data to clipboard
  const handleCopyToClipboard = () => {
    const headerRow = columns.join('\t');
    const dataRows = rows.map(row => 
      columns.map(col => row[col] ?? '').join('\t')
    ).join('\n');
    
    const tableText = `${headerRow}\n${dataRows}`;
    navigator.clipboard.writeText(tableText);
    
    setCopiedTooltip(true);
    setTimeout(() => setCopiedTooltip(false), 2000);
  };

  // Export as CSV
  const handleExportCSV = () => {
    const csvContent = [
      columns.join(','),
      ...rows.map(row => 
        columns.map(col => {
          const value = row[col] ?? '';
          // Escape values containing commas or quotes
          if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `query_results_${Date.now()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!data || rows.length === 0) {
    return null;
  }

  return (
    <Paper 
      elevation={2} 
      sx={{ 
        mt: 2, 
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Collapsible Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 1.5,
          backgroundColor: 'grey.50',
          borderBottom: expanded ? '1px solid' : 'none',
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'grey.100',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableIcon color="primary" />
          <Typography variant="subtitle1" fontWeight="medium">
            {title}
          </Typography>
          <Chip 
            label={`${rows.length} rows`} 
            size="small" 
            color="primary"
            variant="outlined"
          />
          {columns.length > 0 && (
            <Chip 
              label={`${columns.length} columns`} 
              size="small" 
              variant="outlined"
            />
          )}
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {expanded && (
            <>
              <Tooltip title={copiedTooltip ? "Copied!" : "Copy to clipboard"}>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyToClipboard();
                  }}
                >
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export as CSV">
                <IconButton 
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleExportCSV();
                  }}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Collapsible Table */}
      <Collapse in={expanded}>
        <TableContainer 
          sx={{ 
            maxHeight: 400,
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.100',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'grey.400',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: 'grey.500',
              },
            },
          }}
        >
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontWeight: 'bold',
                      borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                      '&:last-child': {
                        borderRight: 'none',
                      },
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:nth-of-type(odd)': {
                      backgroundColor: 'grey.50',
                    },
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column}
                      sx={{
                        borderRight: '1px solid',
                        borderColor: 'divider',
                        '&:last-child': {
                          borderRight: 'none',
                        },
                      }}
                    >
                      {formatCellValue(row[column])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Footer with summary */}
        {rows.length > 10 && (
          <Box
            sx={{
              p: 1,
              backgroundColor: 'grey.50',
              borderTop: '1px solid',
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Showing {rows.length} rows • Scroll to see more
            </Typography>
          </Box>
        )}
      </Collapse>
    </Paper>
  );
};

// Helper function to format cell values
const formatCellValue = (value) => {
  if (value === null || value === undefined) {
    return <Typography variant="body2" color="text.disabled">—</Typography>;
  }
  
  if (typeof value === 'boolean') {
    return value ? '✓' : '✗';
  }
  
  if (typeof value === 'number') {
    // Format large numbers with commas
    if (Math.abs(value) >= 1000) {
      return value.toLocaleString();
    }
    // Round decimals to 2 places
    if (!Number.isInteger(value)) {
      return value.toFixed(2);
    }
  }
  
  // Truncate long strings
  if (typeof value === 'string' && value.length > 100) {
    return (
      <Tooltip title={value}>
        <span>{value.substring(0, 100)}...</span>
      </Tooltip>
    );
  }
  
  return String(value);
};

export default DataTable;